import React, { useRef } from 'react';
import { useSelector } from 'react-redux';
import { Card, CardContent, Box, Typography, LinearProgress, Grid } from '@mui/material';
import { motion } from 'framer-motion';
// import TrendingUpIcon from '@mui/icons-material/TrendingUp';

const FeatureCard = ({ title, features, maxFrequency, showWorkaroundText = false, gradient = 'default' }) => (
  <motion.div whileHover={{ scale: 1.015 }} transition={{ duration: 0.2 }}>
    <Card
      sx={{
        height: '100%',
        borderRadius: 2,
        position: 'relative',
        isolation: 'isolate',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          padding: '1px',
          borderRadius: 'inherit',
          background:
            gradient === 'secondary'
              ? 'linear-gradient(135deg, rgba(255,107,107,0.5) 0%, rgba(103,58,183,0.5) 60%, rgba(103,58,183,0.2) 100%)'
              : 'linear-gradient(135deg, rgba(0,118,255,0.5) 0%, rgba(103,58,183,0.5) 60%, rgba(103,58,183,0.2) 100%)',
          WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          WebkitMaskComposite: 'xor',
          maskComposite: 'exclude',
          zIndex: -1
        },
        boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: '0 4px 8px rgba(0,0,0,0.03)'
        },
        bgcolor: 'background.paper'
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 600,
            mb: 2,
            ...(gradient === 'secondary'
              ? {
                  background: 'linear-gradient(135deg, #0070f3 0%, #6743b5 60%, rgba(103,58,183,0.7) 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent'
                }
              : { color: 'primary.main' })
          }}
        >
          {title}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {features.map((feature, index) => (
            <Box key={index}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {feature.name}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ flexGrow: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={(feature.frequency / maxFrequency) * 100}
                    sx={{
                      height: 8,
                      borderRadius: 5,
                      backgroundColor: 'secondary.lighter',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 5,
                        backgroundColor: 'primary.main'
                      }
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 56 }}>
                  <Typography variant="caption" sx={{ fontWeight: 500 }}>
                    {feature.frequency}
                  </Typography>
                </Box>
              </Box>

              {showWorkaroundText && feature.workaroundText && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mt: 1,
                    ml: 2,
                    fontStyle: 'italic',
                    borderLeft: '2px solid',
                    borderColor: 'secondary.light',
                    pl: 2,
                    py: 1
                  }}
                >
                  &quot;{feature.workaroundText}&quot;
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  </motion.div>
);

const RequestedFeatures = () => {
  const feature_requests = useSelector((state) => state.reviewsReducer.v2Data.review_analysis_results.feature_requests);
  const contentRef = useRef(null);

  if (!feature_requests) return null;

  // Format formal requests
  const formalRequests = (feature_requests.formal_requests || []).map((item) => ({
    name: item.request,
    frequency: item.frequency,
    priority: item.urgency,
    business_value: item.business_value
  }));

  // Format workarounds
  const workarounds = (feature_requests.workarounds || []).map((item) => ({
    name: item.issue,
    frequency: item.frequency,
    priority: item.severity,
    business_value: item.business_impact,
    workaroundText: item.workaround
  }));

  // Find max frequency across all features for percentage calculation
  const maxFrequency = Math.max(...formalRequests.map((f) => f.frequency), ...workarounds.map((f) => f.frequency));

  // Sort by frequency
  const sortedRequests = [...formalRequests].sort((a, b) => b.frequency - a.frequency);
  const sortedWorkarounds = [...workarounds].sort((a, b) => b.frequency - a.frequency);

  return (
    <Box sx={{ mb: 4 }} ref={contentRef}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', my: 1 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            background: 'linear-gradient(135deg, #0070f3 0%, #6743b5 60%, rgba(103,58,183,0.7) 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent'
          }}
        >
          Most Requested Features
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
        Top feature requests and common workarounds
      </Typography>
      <Grid container spacing={3}>
        {sortedRequests.length > 0 && (
          <Grid item xs={12} md={6}>
            <FeatureCard title="Feature Requests" features={sortedRequests} maxFrequency={maxFrequency} />
          </Grid>
        )}

        {sortedWorkarounds.length > 0 && (
          <Grid item xs={12} md={6}>
            <FeatureCard
              title="Common Workarounds"
              features={sortedWorkarounds}
              maxFrequency={maxFrequency}
              showWorkaroundText={true}
              gradient="secondary"
            />
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default RequestedFeatures;
